import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Search, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageWrapper } from '../components/PageWrapper';
import { JournalAttachmentImage } from '../components/JournalAttachmentImage';
import {
  getEntries,
  getSettings,
  formatDisplayDate,
  formatDate,
  isAICompanionEnabled,
  getHistoryWeeklySummaryCache,
  setHistoryWeeklySummaryCache,
  saveEntry,
} from '@/utils/storage';
import { generateHistorySummary, getHistorySummaryFirestoreKey } from '@/utils/aiCompanion';
import { deleteCloudJournalPhoto } from '@/utils/cloudApi';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const sundayYmd = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return formatDate(d);
};

const rowToSummaryRow = (e) => {
  const { date, ...rest } = e;
  return { date, entry: rest };
};

/** Entries for AI summaries: calendar week (Sun–Sat), month, or year of the latest journal day. */
const entriesForSummaryRange = (range, sortedEntriesNewestFirst) => {
  const list = Array.isArray(sortedEntriesNewestFirst) ? sortedEntriesNewestFirst : [];
  if (!list.length) return [];
  const anchor = String(list[0]?.date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(anchor)) return list.slice(0, 7).map(rowToSummaryRow);
  if (range === 'weekly') {
    const sun = sundayYmd(anchor);
    return list.filter((e) => sundayYmd(e.date) === sun).map(rowToSummaryRow);
  }
  if (range === 'monthly') {
    const ym = anchor.slice(0, 7);
    return list.filter((e) => String(e.date || '').slice(0, 7) === ym).map(rowToSummaryRow);
  }
  const y = anchor.slice(0, 4);
  return list.filter((e) => String(e.date || '').startsWith(y)).map(rowToSummaryRow);
};

const hasClosedWeeklyWindow = (sortedEntriesNewestFirst) => {
  const list = Array.isArray(sortedEntriesNewestFirst) ? sortedEntriesNewestFirst : [];
  if (!list.length) return false;
  const latestDate = String(list[0]?.date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(latestDate)) return false;
  const latestWeekKey = sundayYmd(latestDate);
  const currentWeekKey = sundayYmd(formatDate(new Date()));
  if (!latestWeekKey || !currentWeekKey) return false;
  if (latestWeekKey !== currentWeekKey) return true;
  // Allow an in-progress weekly summary once enough current-week signal exists.
  const currentWeekDays = new Set(
    list
      .filter((e) => sundayYmd(String(e?.date || '').trim()) === latestWeekKey)
      .map((e) => String(e?.date || '').trim())
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
  );
  return currentWeekDays.size >= 3;
};

const HistoryEntryPhotoThumbButton = ({ entryDate, firstPhoto, onSelectPhoto }) => {
  const [hideThumb, setHideThumb] = useState(false);
  if (hideThumb) return null;
  return (
    <button
      type="button"
      onClick={() => onSelectPhoto({ date: entryDate, attachment: firstPhoto })}
      className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-theme-secondary focus:outline-none focus:ring-2 focus:ring-wisteria-200"
      aria-label="View photo"
    >
      <JournalAttachmentImage
        url={firstPhoto.url}
        storagePath={firstPhoto.storagePath}
        alt=""
        className="w-full h-full object-cover bg-wisteria-50/30"
        onUnavailable={() => setHideThumb(true)}
      />
    </button>
  );
};

export const History = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [weeklySummary, setWeeklySummary] = useState('');
  const [activeSummaryRange, setActiveSummaryRange] = useState('weekly');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [weeklySummaryFailed, setWeeklySummaryFailed] = useState(false);
  const [summaryGeneratedAt, setSummaryGeneratedAt] = useState('');
  const [dayTick, setDayTick] = useState(0);
  const [summaryRefreshNonce, setSummaryRefreshNonce] = useState(0);
  const forceRegenerateSummaryRef = useRef(false);
  const [photoLightbox, setPhotoLightbox] = useState(null);
  const [photoDeleteConfirmOpen, setPhotoDeleteConfirmOpen] = useState(false);
  const settings = getSettings();

  const photoLightboxActionClass =
    'flex-1 min-h-[48px] rounded-2xl px-4 py-3 bg-theme-primary text-primary font-body text-sm border border-theme-secondary shadow-ambient';

  const loadEntries = () => {
    const allEntries = getEntries();
    const entriesArray = Object.entries(allEntries)
      .map(([date, entry]) => ({ date, ...entry }))
      .sort((a, b) => b.date.localeCompare(a.date));
    setEntries(entriesArray);
  };

  useEffect(() => {
    loadEntries();

    const handleFocus = () => loadEntries();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadEntries();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setDayTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!photoLightbox) {
      setPhotoDeleteConfirmOpen(false);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (photoDeleteConfirmOpen) setPhotoDeleteConfirmOpen(false);
      else setPhotoLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoLightbox, photoDeleteConfirmOpen]);

  useEffect(() => {
    let isActive = true;

    const getWeekKey = (date = new Date()) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
    };

    const loadWeeklySummary = async () => {
      const forceRegenerate = forceRegenerateSummaryRef.current;
      forceRegenerateSummaryRef.current = false;
      if (entries.length === 0) {
        if (isActive) {
          setWeeklySummary('');
          setSummaryGeneratedAt('');
          setWeeklySummaryFailed(false);
          setSummaryLoading(false);
        }
        return;
      }

      const uniqueDaysCount = new Set(entries.map((e) => e.date)).size;
      const weeklyWindowReady = hasClosedWeeklyWindow(entries);
      const availability = {
        weekly: weeklyWindowReady,
        monthly: uniqueDaysCount >= 14,
        yearly: uniqueDaysCount >= 60,
      };
      const range = activeSummaryRange;
      if (!availability[range]) {
        if (isActive) {
          setWeeklySummary('');
          setSummaryGeneratedAt('');
          setWeeklySummaryFailed(false);
          setSummaryLoading(false);
        }
        return;
      }

      const summaryRows = entriesForSummaryRange(range, entries);
      const aiEnabled = isAICompanionEnabled();

      if (!aiEnabled) {
        if (isActive) {
          setWeeklySummary('');
          setSummaryGeneratedAt('');
          setWeeklySummaryFailed(false);
          setSummaryLoading(false);
        }
        return;
      }

      const today = formatDate(new Date());
      const weekKey = getWeekKey(new Date());
      const summaryKey = getHistorySummaryFirestoreKey(range, summaryRows);
      const cached = getHistoryWeeklySummaryCache();
      const cachedText = String(cached.summaries?.[range] || '').trim();
      const cachedRangeKey = String(cached.rangeKeys?.[range] || '').trim();
      const legacyWeeklyHit = range === 'weekly'
        && !cachedRangeKey
        && cached.weekKey === weekKey
        && cachedText;
      const cacheHit = cachedRangeKey === summaryKey && cachedText;
      if (!forceRegenerate && (cacheHit || legacyWeeklyHit)) {
        if (isActive) {
          setWeeklySummary(cachedText);
          setSummaryGeneratedAt(String(cached.generatedAtByRange?.[range] || ''));
          setWeeklySummaryFailed(false);
          setSummaryLoading(false);
        }
        return;
      }

      if (isActive) {
        setSummaryLoading(true);
        setWeeklySummaryFailed(false);
      }

      try {
        const result = await generateHistorySummary(
          range,
          summaryRows,
          {
            onStreamDelta: (partial) => isActive && setWeeklySummary(partial),
            forceRegenerate,
          },
        );

        if (!isActive) return;

        if (result.success && result.response) {
          setWeeklySummary(result.response);
          setSummaryGeneratedAt(
            String(result.generatedAt || '').trim() || new Date().toISOString(),
          );
          setWeeklySummaryFailed(false);
          setHistoryWeeklySummaryCache(today, result.response, {
            range,
            weekKey,
            rangeKey: summaryKey,
          });
        } else {
          setWeeklySummary('');
          setSummaryGeneratedAt('');
          setWeeklySummaryFailed(true);
        }
      } catch {
        if (isActive) {
          setWeeklySummary('');
          setSummaryGeneratedAt('');
          setWeeklySummaryFailed(true);
        }
      } finally {
        if (isActive) {
          setSummaryLoading(false);
        }
      }
    };

    void loadWeeklySummary();

    return () => {
      isActive = false;
    };
  }, [entries, dayTick, activeSummaryRange, summaryRefreshNonce]);

  useEffect(() => {
    const uniqueDays = new Set(entries.map((e) => e.date)).size;
    const available = {
      weekly: hasClosedWeeklyWindow(entries),
      monthly: uniqueDays >= 14,
      yearly: uniqueDays >= 60,
    };
    if (available[activeSummaryRange]) return;
    if (available.weekly) setActiveSummaryRange('weekly');
    else if (available.monthly) setActiveSummaryRange('monthly');
    else if (available.yearly) setActiveSummaryRange('yearly');
  }, [entries, activeSummaryRange]);

  const getGoalsForEntry = (entry) =>
    entry.goalSnapshots || (settings?.goals || []).sort((a, b) =>
      String(a.createdAt || '').localeCompare(String(b.createdAt || '')),
    );

  const getGoalDisplay = (entry, goal) => {
    const status = entry.goals?.[goal.id];
    if (status?.completed || status?.easyActivity) return { text: cap(goal.text), done: true };
    return null;
  };

  const entrySearchText = (entry) => {
    const goalsText = getGoalsForEntry(entry)
      .map((goal) => {
        const status = entry.goals?.[goal.id];
        if (!status?.completed && !status?.easyActivity) return null;
        return `${goal.text || ''}`;
      })
      .filter(Boolean)
      .join(' ');

    const reflectionText = [...Array(10)]
      .map((_, idx) => entry[`answer${idx + 1}`])
      .filter((v) => v && v !== '–')
      .join(' ');

    return [entry.date, goalsText, reflectionText, entry.aiResponse || '', entry.homeChatCoachSummary || '']
      .join(' ')
      .toLowerCase();
  };

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((entry) => entrySearchText(entry).includes(q));
  }, [entries, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedEntries = useMemo(() => {
    const groups = {};

    filteredEntries.forEach((entry) => {
      const dt = new Date(`${entry.date}T00:00:00`);
      if (Number.isNaN(dt.getTime())) {
        if (!groups.Unknown) groups.Unknown = [];
        groups.Unknown.push(entry);
        return;
      }

      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    });

    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, list]) => {
        if (key === 'Unknown') {
          return { key, label: 'Unknown Date', entries: list };
        }

        const [year, month] = key.split('-').map(Number);
        const label = new Date(year, month - 1, 1).toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        });

        return { key, label, entries: list };
      });
  }, [filteredEntries]);

  useEffect(() => {
    if (groupedEntries.length === 0) return;

    const isSearching = searchQuery.trim().length > 0;
    if (isSearching) {
      const openAllMatching = {};
      groupedEntries.forEach((group) => {
        openAllMatching[group.key] = true;
      });
      setExpandedMonths(openAllMatching);
      return;
    }

    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const initial = {};
    groupedEntries.forEach((group, index) => {
      initial[group.key] = group.key === currentKey || (index === 0 && !groupedEntries.some((g) => g.key === currentKey));
    });
    setExpandedMonths(initial);
  }, [groupedEntries, searchQuery]);

  const toggleMonth = (key) => {
    setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAllMonths = () => {
    const next = {};
    groupedEntries.forEach((group) => {
      next[group.key] = true;
    });
    setExpandedMonths(next);
  };

  const collapseAllMonths = () => {
    const next = {};
    groupedEntries.forEach((group) => {
      next[group.key] = false;
    });
    setExpandedMonths(next);
  };

  const handleDeletePhoto = async (dateStr, photoId) => {
    try {
      await deleteCloudJournalPhoto(dateStr, photoId);
      const row = getEntries()[dateStr] || {};
      const prev = Array.isArray(row.attachments) ? row.attachments : [];
      const next = prev.filter((a) => String(a?.id) !== String(photoId));
      saveEntry(dateStr, { ...row, attachments: next });
      loadEntries();
      setPhotoLightbox(null);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(String(e?.message || e || 'Could not delete photo'));
    }
  };

  const uniqueDaysCount = new Set(entries.map((e) => e.date)).size;
  const weeklyWindowReady = hasClosedWeeklyWindow(entries);
  const canShowAnySummary = weeklyWindowReady || uniqueDaysCount >= 14 || uniqueDaysCount >= 60;

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-8">
        {entries.length > 0 && canShowAnySummary && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 }}
            className="bg-theme-light rounded-2xl p-5 shadow-ambient border border-theme-secondary mb-4"
            data-testid="history-weekly-summary"
          >
            <h2 className="font-heading text-lg text-primary mb-3">
              {activeSummaryRange === 'weekly'
                ? 'Your Weekly Summary'
                : activeSummaryRange === 'monthly'
                  ? 'Your Monthly Summary'
                  : 'Your Yearly Summary'}
            </h2>
            {(() => {
              const showWeekly = weeklyWindowReady;
              const showMonthly = uniqueDaysCount >= 14;
              const showYearly = uniqueDaysCount >= 60;
              const btn = (range, label) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => setActiveSummaryRange(range)}
                  className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${
                    activeSummaryRange === range
                      ? 'bg-theme-primary text-primary border-theme-primary'
                      : 'bg-theme-light text-secondary border-theme-secondary hover:text-primary'
                  }`}
                >
                  {label}
                </button>
              );
              return (
                <div className="flex flex-wrap gap-2 mb-4 justify-center items-center w-full">
                  {showWeekly ? btn('weekly', 'Weekly') : null}
                  {showMonthly ? btn('monthly', 'Monthly') : null}
                  {showYearly ? btn('yearly', 'Yearly') : null}
                </div>
              );
            })()}
            {!isAICompanionEnabled() ? (
              <p className="font-body text-sm text-secondary leading-relaxed">
                Turn on the AI companion in Settings to see a weekly reflection here.
              </p>
            ) : summaryLoading ? (
              <div className="flex items-center gap-2 text-secondary">
                <Loader2 size={16} className="animate-spin" />
                <span className="font-body text-sm">Creating your summary...</span>
              </div>
            ) : weeklySummary ? (
              <>
                <div className="space-y-3">
                  {weeklySummary
                    .split(/\n\n+/)
                    .map((para) => para.replace(/\n/g, ' ').trim())
                    .filter(Boolean)
                    .map((para, idx) => (
                      <p key={idx} className="font-body text-sm text-primary leading-relaxed">
                        {para}
                      </p>
                    ))}
                </div>
                {summaryGeneratedAt ? (
                  <div className="mt-3">
                    <p className="font-body text-xs text-secondary">
                      Generated {new Date(summaryGeneratedAt).toLocaleString()}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        forceRegenerateSummaryRef.current = true;
                        setSummaryRefreshNonce((n) => n + 1);
                      }}
                      className="font-body text-xs text-secondary underline mt-1"
                      data-testid="history-regenerate-summary"
                    >
                      Regenerate summary
                    </button>
                  </div>
                ) : null}
              </>
            ) : weeklySummaryFailed ? (
              <p className="font-body text-sm text-secondary leading-relaxed">
                AI is not available right now. Please try again shortly—you can reopen this screen in a moment.
              </p>
            ) : (
              <p className="font-body text-sm text-secondary leading-relaxed">
                No summary text returned yet.
              </p>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="relative mb-6"
        >
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search entries"
            className="w-full rounded-2xl border border-theme-secondary bg-theme-light pl-11 pr-4 py-3 font-body text-sm text-primary placeholder:text-secondary/70 shadow-ambient focus:outline-none focus:ring-2 focus:ring-wisteria-200"
            data-testid="history-search"
          />
        </motion.div>

        {entries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center min-h-[50vh] flex flex-col items-center justify-center"
            data-testid="empty-history"
          >
            <p className="text-secondary font-body text-sm mb-2">No entries yet</p>
            <p className="text-secondary/60 font-body text-sm">Your daily logs will appear here</p>
          </motion.div>
        ) : filteredEntries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center py-12"
            data-testid="empty-history-search"
          >
            <p className="text-secondary font-body text-sm mb-1">No matches found</p>
            <p className="text-secondary/60 font-body text-sm">Try a different keyword</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {groupedEntries.length > 1 && searchQuery.trim().length === 0 && (
              <div className="flex items-center justify-end gap-2 mb-1">
                <button
                  type="button"
                  onClick={expandAllMonths}
                  className="text-xs font-body px-3 py-1.5 rounded-full bg-theme-light border border-theme-secondary text-secondary hover:text-primary transition-colors"
                  data-testid="history-expand-all"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={collapseAllMonths}
                  className="text-xs font-body px-3 py-1.5 rounded-full bg-theme-light border border-theme-secondary text-secondary hover:text-primary transition-colors"
                  data-testid="history-collapse-all"
                >
                  Collapse all
                </button>
              </div>
            )}

            {groupedEntries.map((group, groupIndex) => (
              <div key={group.key} className="space-y-3">
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.04 }}
                  onClick={() => toggleMonth(group.key)}
                  className="w-full flex items-center justify-between px-1 py-1"
                  data-testid={`history-group-toggle-${group.key}`}
                >
                  <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70">
                    {group.label}
                  </h2>
                  <ChevronDown
                    size={14}
                    className={`text-secondary transition-transform ${expandedMonths[group.key] ? 'rotate-180' : ''}`}
                  />
                </motion.button>

                {expandedMonths[group.key]
                  && group.entries.map((entry) => {
                    const entryGoals = getGoalsForEntry(entry);
                    const photoThumbs = Array.isArray(entry.attachments)
                      ? entry.attachments.filter((a) => a && (a.url || a.storagePath))
                      : [];
                    const firstPhoto = photoThumbs[0];
                    return (
                      <div
                        key={entry.date}
                        className="w-full bg-theme-light rounded-2xl p-5 shadow-ambient border border-theme-secondary transition-all hover:-translate-y-0.5 hover:shadow-interactive"
                      >
                        <div className="flex items-start justify-between gap-3">
                          {firstPhoto ? (
                            <HistoryEntryPhotoThumbButton
                              entryDate={entry.date}
                              firstPhoto={firstPhoto}
                              onSelectPhoto={setPhotoLightbox}
                            />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => navigate(`/historical-view/${entry.date}`)}
                            className="flex-1 min-w-0 text-left flex items-start justify-between gap-2"
                            data-testid={`history-entry-${entry.date}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-body text-sm text-secondary mb-3">
                                {formatDisplayDate(entry.date)}
                              </p>
                              {entryGoals.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {entryGoals.map((goal) => {
                                    const display = getGoalDisplay(entry, goal);
                                    if (!display) return null;
                                    return (
                                      <span
                                        key={goal.id}
                                        className="text-xs font-body px-2 py-1 rounded-full bg-wisteria-100 text-wisteria-700"
                                      >
                                        ✓ {display.text}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : null}
                              {photoThumbs.length > 1 ? (
                                <p className="font-body text-[11px] text-secondary/70 mt-2">
                                  +{photoThumbs.length - 1} more photo{photoThumbs.length - 1 > 1 ? 's' : ''}
                                </p>
                              ) : null}
                            </div>
                            <ChevronRight size={20} strokeWidth={1.5} className="text-wisteria-300 flex-shrink-0 ml-1" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
        )}
      </div>

      {photoLightbox ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          {/* Full-screen hit target below content so iOS/WKWebView always receives tap-to-dismiss (nav is z-[100]). */}
          <button
            type="button"
            className="absolute inset-0 z-0 cursor-default border-0 bg-black/70 p-0"
            onClick={() => {
              if (!photoDeleteConfirmOpen) setPhotoLightbox(null);
            }}
            aria-label="Close photo preview"
          />
          <div className="relative z-10 flex w-full max-w-md max-h-[85vh] flex-col items-center gap-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <div className="max-w-full rounded-lg">
              <JournalAttachmentImage
                url={photoLightbox.attachment.url}
                storagePath={photoLightbox.attachment.storagePath}
                alt=""
                className="max-h-[55vh] w-auto max-w-full rounded-lg object-contain"
                onUnavailable={() => setPhotoLightbox(null)}
              />
            </div>
            <div className="flex w-full max-w-sm gap-3">
              <button
                type="button"
                className={photoLightboxActionClass}
                onClick={() => setPhotoLightbox(null)}
              >
                Close
              </button>
              <button
                type="button"
                className={photoLightboxActionClass}
                onClick={() => setPhotoDeleteConfirmOpen(true)}
              >
                Delete
              </button>
            </div>
          </div>
          {photoDeleteConfirmOpen ? (
            <div
              className="absolute inset-0 z-[220] flex items-center justify-center bg-black/60 p-4"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="history-photo-delete-confirm-title"
            >
              <div className="w-full max-w-sm rounded-2xl bg-theme-light border border-theme-secondary p-6 shadow-lg">
                <p
                  id="history-photo-delete-confirm-title"
                  className="font-body text-sm text-primary text-center mb-5"
                >
                  Are you sure you want to delete this photo?
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className={photoLightboxActionClass}
                    onClick={() => setPhotoDeleteConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={photoLightboxActionClass}
                    onClick={() => {
                      setPhotoDeleteConfirmOpen(false);
                      void handleDeletePhoto(
                        photoLightbox.date,
                        String(photoLightbox.attachment.id || '').trim(),
                      );
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </PageWrapper>
  );
};

export default History;
